import { Dayjs } from "dayjs"
import { IReading } from "@/models/meterReading/meterReading.types"
import { IRegister } from "@/models/meterRegister/meterRegister.types"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import {
  EstimatorFuelType,
  EstimatorMode,
  IRegisterEstimate,
  VolumeUnit,
} from "@/models/meterReadEstimate/meterReadEstimate.types"

/**
 * Gates every part of this journey, including the tab swap in the renewals
 * usage calculator, so the old path is one switch away. Retired by SO-32439.
 */
export const METER_READ_ESTIMATOR_UI_FLAG =
  "TMP_SO_32377_METER_READ_ESTIMATOR_UI"

/**
 * The three things an agent can ask the estimator for. Two of them share a
 * backend mode (TWO_READS) and differ only in which field of the response
 * answers the question.
 */
export enum EstimatorUiMode {
  ReadFromTwoReads = "READ_FROM_TWO_READS",
  ReadFromEacAq = "READ_FROM_EAC_AQ",
  EacAqFromTwoReads = "EAC_AQ_FROM_TWO_READS",
}

export const ESTIMATOR_MODE_TITLES: Record<EstimatorUiMode, string> = {
  [EstimatorUiMode.ReadFromTwoReads]: "2 reads → read estimate",
  [EstimatorUiMode.ReadFromEacAq]: "1 read & {eacOrAq} → read estimate",
  [EstimatorUiMode.EacAqFromTwoReads]: "2 reads → {eacOrAq} estimate",
}

/** Our reads are whole units in practice, whatever Junifer's decimal_places says. */
export const DISPLAY_DECIMAL_PLACES = 0

export const usesTwoReads = (mode: EstimatorUiMode) =>
  mode !== EstimatorUiMode.ReadFromEacAq

export const usesTargetDate = (mode: EstimatorUiMode) =>
  mode !== EstimatorUiMode.EacAqFromTwoReads

export const toApiMode = (mode: EstimatorUiMode): EstimatorMode =>
  usesTwoReads(mode) ? EstimatorMode.TwoReads : EstimatorMode.KnownReadPlusEacAq

export const toFuelType = (meterpoint: IMeterpoint): EstimatorFuelType =>
  meterpoint.isElectricity
    ? EstimatorFuelType.Electricity
    : EstimatorFuelType.Gas

/**
 * Nova labels gas AQ and electricity EAC. Both EAC and AC are live for
 * electricity through the MHHS transition and Nova does not hold the migration
 * state, so electricity shows both rather than guessing.
 */
export const eacOrAqLabel = (meterpoint: IMeterpoint): string =>
  meterpoint.isElectricity ? "EAC/AC" : "AQ"

/** Excludes system-generated and estimated reads, as the rest of Nova does. */
export const isActualRead = (reading: IReading): boolean =>
  !reading.isSystemReading && !reading.isEstimated

/**
 * Whether two meter point objects put the same registers in front of the agent.
 * A refetch of the same meter point does. Another meter point, or a meter
 * exchange that changed its registers, does not.
 */
export const isSameRegisterSet = (
  meterpoint: IMeterpoint,
  previous: IMeterpoint | undefined,
): boolean =>
  meterpoint.id === previous?.id &&
  meterpoint.registers.length === previous.registers.length &&
  meterpoint.registers.every(
    (register, index) => register.id === previous.registers[index].id,
  )

export const actualReadsNewestFirst = (register: IRegister): IReading[] =>
  register.readings
    .filter(isActualRead)
    .sort((a, b) => (a.date.isAfter(b.date) ? -1 : 1))

const VOLUME_UNIT_BY_READ_UNIT: Record<string, VolumeUnit> = {
  m3: VolumeUnit.CubicMetres,
  hcf: VolumeUnit.HundredsCubicFeet,
  // Junifer's thm³ is thousands of cubic metres, not therms (SO-32714)
  thm3: VolumeUnit.ThousandsCubicMetres,
  "thm³": VolumeUnit.ThousandsCubicMetres,
}

/**
 * The unit the register's reads carry, or null where we cannot tell. It used to
 * fall back to cubic metres, which is wrong roughly a third of the time on an
 * imperial meter and said nothing about being a guess. Nobody assumes now: a
 * null means the agent is asked (SO-32714).
 */
export const volumeUnitFor = (register: IRegister): VolumeUnit | null =>
  VOLUME_UNIT_BY_READ_UNIT[register.unit?.toLowerCase() ?? ""] ?? null

/**
 * Whether the meter point tells us the unit outright. Where it does, the unit
 * is shown rather than offered as a choice - it is a property of the meter, not
 * a decision for the agent. The selector only appears where the register's
 * reads carry a unit we do not recognise, or carry none at all.
 */
export const hasKnownVolumeUnit = (register: IRegister): boolean =>
  (register.unit?.toLowerCase() ?? "") in VOLUME_UNIT_BY_READ_UNIT

/**
 * At 1,000 m³ a unit, a register recording in thousands of cubic metres moves
 * by one or two units a year on a domestic meter. Its estimates are correctly
 * scaled but coarse, so the agent is told rather than blocked.
 */
export const isCoarseVolumeUnit = (unit: VolumeUnit | null): boolean =>
  unit === VolumeUnit.ThousandsCubicMetres

/** Short forms for inline use, e.g. "8 m³ (~89 kWh) over 32 days". */
export const VOLUME_UNIT_SHORT_LABELS: Record<VolumeUnit, string> = {
  [VolumeUnit.CubicMetres]: "m³",
  [VolumeUnit.HundredsCubicFeet]: "hcf",
  [VolumeUnit.ThousandsCubicFeet]: "thcf",
  [VolumeUnit.ThousandsCubicMetres]: "thm³",
}

/**
 * Whole units, no zero-padding. The result is a single number field, not a row
 * of dials, so padding only ever risked showing the wrong width for a register
 * whose digit count we were guessing at.
 */
export const formatRead = (value: number): string => String(Math.round(value))

/**
 * The value to show an agent: the backend's own wrapped and rounded figure,
 * never the raw projection. SO-32399 made this authoritative, so the UI no
 * longer second-guesses it by measuring the width itself - which it could only
 * ever do against an assumed five digits anyway.
 */
export const displayRead = (estimate: IRegisterEstimate): string =>
  formatRead(estimate.estimatedReadFormatted)

/** Under a month between reads makes the annualised figure unreliable. */
export const isShortGap = (earlier: Dayjs, later: Dayjs): boolean =>
  later.diff(earlier, "month", true) < 1
